"use client";

import { useServerErrorMessage } from "@/lib/i18n/use-server-error-message";
import { useUIMessages } from "@convex-dev/agent/react";
import { api } from "@v1/backend/convex/_generated/api";
import { useMutation } from "convex/react";
import { useTranslations } from "next-intl";
import { type ReactElement, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { persistVisitorToken, readStoredVisitorToken } from "../customer-chat/customer-chat-session-storage";
import { MessengerAgentAvatar } from "./messenger-avatars";
import { type MessengerBubbleItem, MessengerBubbles } from "./messenger-bubbles";
import { MessengerComposer } from "./messenger-composer";
import { useMessenger } from "./messenger-context";
import { MessengerDetailHeader } from "./messenger-header";
import { MessengerLoadOlder, MessengerTypingBubble } from "./messenger-thread-parts";
import { isAwaitingAgentAnswer, isFullUIMessage, toMessengerBubble } from "./messenger-ui-message";
import { useMessengerPendingSend } from "./use-messenger-pending-send";
import { useMessengerStickToBottom } from "./use-messenger-scroll";

const PAGE_SIZE = 30;

interface VisitorSession {
  readonly threadId: string;
  readonly token: string;
}

function useVisitorOrg(): { readonly orgSlug: string; readonly orgName: string } {
  const { audience } = useMessenger();
  if (audience.kind !== "visitor") throw new Error("visitor chat rendered for a member");
  return audience;
}

/** Help-center visitor: one anonymous chat, kept by a token in this browser. `askText` is sent once on open. */
export function MessengerVisitorChatView({ askText }: { readonly askText: string | null }): ReactElement {
  const t = useTranslations("messenger.thread");
  const errorMessage = useServerErrorMessage();
  const { config } = useMessenger();
  const { orgSlug, orgName } = useVisitorOrg();
  const initSession = useMutation(api.customerChat.public_widget.initSession);
  const sendMessage = useMutation(api.customerChat.public_messages.sendMessage);
  const [session, setSession] = useState<VisitorSession | null>(null);

  // A visitor who wrote before sees their chat again.
  useEffect(() => {
    const token = readStoredVisitorToken(orgSlug);
    if (token === null) return;
    let cancelled = false;
    void initSession({ org_slug: orgSlug, visitor_token: token })
      .then((result) => {
        if (!cancelled) setSession({ threadId: result.thread_id, token: result.visitor_token });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [initSession, orgSlug]);

  // One list before and after the first message creates the session: nothing blinks away.
  const { results, status, loadMore } = useUIMessages(
    api.customerChat.public_messages.listThreadMessages,
    session === null ? "skip" : { threadId: session.threadId, visitor_token: session.token, org_slug: orgSlug },
    { initialNumItems: PAGE_SIZE, stream: true },
  );
  const rows = useMemo(() => results.filter(isFullUIMessage), [results]);
  const pending = useMessengerPendingSend(rows, session !== null && status !== "LoadingFirstPage");

  const welcome = useMemo(
    (): MessengerBubbleItem[] =>
      config.welcome_message.length > 0
        ? [{ id: "welcome", author: "agent", name: null, text: config.welcome_message, at: null }]
        : [],
    [config.welcome_message],
  );
  const items = useMemo(
    (): MessengerBubbleItem[] => [
      ...welcome,
      ...rows.map(toMessengerBubble).filter((m): m is MessengerBubbleItem => m !== null),
      ...(pending.bubble === null ? [] : [pending.bubble]),
    ],
    [pending.bubble, rows, welcome],
  );
  const awaiting = pending.bubble !== null || isAwaitingAgentAnswer(rows[rows.length - 1]);
  const lastItem = items[items.length - 1];
  const scrollRef = useMessengerStickToBottom(
    `${items.length}:${lastItem?.text.length ?? 0}:${lastItem?.tools?.length ?? 0}:${awaiting}`,
  );

  const send = (text: string): Promise<boolean> =>
    pending.track(text, async () => {
      try {
        let current = session;
        if (current === null) {
          const result = await initSession({
            org_slug: orgSlug,
            visitor_token: readStoredVisitorToken(orgSlug) ?? undefined,
          });
          persistVisitorToken(orgSlug, result.visitor_token);
          current = { threadId: result.thread_id, token: result.visitor_token };
          setSession(current);
        }
        await sendMessage({ org_slug: orgSlug, thread_id: current.threadId, visitor_token: current.token, text });
        return true;
      } catch (error) {
        toast.error(errorMessage(error, t("fehler")));
        return false;
      }
    });

  const sendRef = useRef(send);
  sendRef.current = send;
  const asked = useRef<boolean>(false);
  useEffect(() => {
    if (askText === null || asked.current) return;
    asked.current = true;
    void sendRef.current(askText);
  }, [askText]);

  return (
    <>
      <MessengerDetailHeader title={orgName} leading={<MessengerAgentAvatar size="md" />} />
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {status === "CanLoadMore" ? <MessengerLoadOlder onClick={() => loadMore(PAGE_SIZE)} /> : null}
        <MessengerBubbles items={items} footer={awaiting ? <MessengerTypingBubble /> : null} />
      </div>
      <MessengerComposer autoFocus clearAtOnce onSend={send} />
    </>
  );
}
